<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElButton, ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon, ElTooltip } from 'element-plus'
import {
  Clock,
  Document,
  Expand,
  Fold,
  Lock,
  Location,
  MapLocation,
  Moon,
  SwitchButton,
  Sunny,
  User,
} from '@element-plus/icons-vue'

import AppMobileMenu from '@/components/AppMobileMenu.vue'
import AppNavSection, { type NavItem } from '@/components/AppNavSection.vue'
import GgiLogo from '@/components/GgiLogo.vue'
import { signOut, useSession } from '@/auth/session'
import { useTheme } from '@/composables/useTheme'
import { useSidebarCollapsed } from '@/composables/useSidebarCollapsed'

const { t } = useI18n()
const route = useRoute()
const { theme, toggleTheme } = useTheme()
const session = useSession()

const isMenuOpen = ref(false)
const { collapsed, toggle: toggleSidebar } = useSidebarCollapsed()

// The navigation mirrors the routes. Each entry carries the permission its
// screen needs; entries the session lacks are hidden (the backend still
// checks every endpoint, so this is convenience, not protection).
const allViewerItems: readonly NavItem[] = [
  { name: 'viewer', labelKey: 'nav.items.viewer', icon: MapLocation, permission: 'maps:map:read' },
]

const allAdminItems: readonly NavItem[] = [
  { name: 'admin-plans', labelKey: 'nav.items.plans', icon: Document, permission: 'maps:plan:read' },
  { name: 'admin-properties', labelKey: 'nav.items.properties', icon: Location, permission: 'maps:property:read' },
  { name: 'admin-users', labelKey: 'nav.items.users', icon: User, permission: 'maps:user:read' },
  { name: 'admin-roles', labelKey: 'nav.items.roles', icon: Lock, permission: 'maps:role:read' },
  { name: 'admin-audit', labelKey: 'nav.items.audit', icon: Clock, permission: 'maps:audit:read' },
]

const viewerItems = computed(() => allViewerItems.filter((item) => session.can(item.permission)))
const adminItems = computed(() => allAdminItems.filter((item) => session.can(item.permission)))

// With a single destination there is nothing to navigate: no sidebar, no
// toggle, no hamburger. The plaza selector in the viewer is enough.
const hasNav = computed(() => viewerItems.value.length + adminItems.value.length > 1)

const toggleLabel = computed(() => (collapsed.value ? t('nav.showSidebar') : t('nav.hideSidebar')))

function onShortcut(event: KeyboardEvent): void {
  if (!hasNav.value || event.key.toLowerCase() !== 'b' || !(event.ctrlKey || event.metaKey)) return
  if (event.altKey || event.shiftKey) return
  const target = event.target as HTMLElement | null
  if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return
  event.preventDefault()
  toggleSidebar()
}
onMounted(() => window.addEventListener('keydown', onShortcut))
onBeforeUnmount(() => window.removeEventListener('keydown', onShortcut))

/** Footer of the sidebar: the plazas the user can see. */
const plazasLabel = computed(() => {
  if (session.allProperties.value) return t('nav.allPlazas')
  const names = session.properties.value.map((property) => property.name)
  if (names.length === 0) return t('nav.noPlazas')
  if (names.length <= 2) return names.join(', ')
  return `${names.slice(0, 2).join(', ')} ${t('nav.morePlazas', { count: names.length - 2 })}`
})

const currentTitle = computed(() => {
  const key = route.meta.titleKey
  return key ? t(`views.${key}.title`) : t('app.name')
})

// Closing the menu on navigation is what makes it usable on a phone:
// otherwise the user taps a link and the menu stays covering the content.
watch(
  () => route.fullPath,
  () => {
    isMenuOpen.value = false
  },
)
</script>

<template>
  <div class="flex h-full flex-col">
    <header
      class="flex h-20 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 sm:px-5"
    >
      <!-- Menu trigger, below the lg breakpoint only. -->
      <ElButton
        v-if="hasNav"
        class="touch-target lg:hidden"
        text
        circle
        :aria-label="t('nav.openMenu')"
        aria-haspopup="dialog"
        :aria-expanded="isMenuOpen"
        @click="isMenuOpen = true"
      >
        <ElIcon><Fold /></ElIcon>
      </ElButton>

      <!-- Sidebar toggle, from lg upwards. -->
      <ElTooltip v-if="hasNav" :content="toggleLabel" placement="bottom">
        <ElButton
          class="touch-target hidden lg:inline-flex"
          text
          circle
          data-testid="sidebar-toggle"
          :aria-label="toggleLabel"
          :aria-expanded="!collapsed"
          @click="toggleSidebar"
        >
          <ElIcon>
            <Expand v-if="collapsed" />
            <Fold v-else />
          </ElIcon>
        </ElButton>
      </ElTooltip>

      <!-- The full official logo. The design system makes the brand red the
           single primary colour; with it nowhere on the shell the product
           reads as a generic admin template rather than as a GGI tool. The
           wordmark needs the height to stay legible, hence the taller header. -->
      <div class="flex min-w-0 items-center gap-3">
        <GgiLogo class="h-[68px] w-auto shrink-0 select-none" />

        <!-- Title hidden on phones: the logo takes priority there. -->
        <div class="hidden min-w-0 items-center gap-3 sm:flex">
          <span class="h-9 w-px shrink-0 bg-border" aria-hidden="true" />
          <div class="min-w-0">
            <p class="heading-sm truncate text-ink">{{ t('app.name') }}</p>
            <p class="body-xs truncate text-ink-secondary">{{ currentTitle }}</p>
          </div>
        </div>
      </div>

      <div class="ml-auto flex items-center gap-1">
        <ElButton
          class="touch-target"
          text
          circle
          :aria-label="t('nav.toggleTheme')"
          @click="toggleTheme"
        >
          <ElIcon>
            <Sunny v-if="theme === 'dark'" />
            <Moon v-else />
          </ElIcon>
        </ElButton>

        <!-- Dev mode: Entra is not configured, nobody is signed in. -->
        <span
          v-if="session.isDevMode.value"
          class="body-xs inline-flex items-center gap-2 rounded-full border border-warning-500/30 bg-warning-50 px-3 py-1 text-warning-500"
          :title="t('session.devModeHint')"
          data-testid="dev-badge"
        >
          <span class="size-1.5 rounded-full bg-current" aria-hidden="true" />
          <span class="hidden sm:inline">{{ t('session.devMode') }}</span>
          <span class="sm:hidden">{{ t('session.notSignedIn') }}</span>
        </span>

        <!-- Backend without a database (development only): say so to the admin. -->
        <span
          v-if="session.me.value?.authMode === 'entra-no-db'"
          class="body-xs inline-flex items-center gap-2 rounded-full border border-warning-500/30 bg-warning-50 px-3 py-1 text-warning-500"
          :title="t('session.noDbHint')"
          data-testid="no-db-badge"
        >
          <span class="size-1.5 rounded-full bg-current" aria-hidden="true" />
          {{ t('session.noDb') }}
        </span>

        <ElDropdown v-if="!session.isDevMode.value" trigger="click" placement="bottom-end" data-testid="user-menu">
          <button
            type="button"
            class="touch-target body-sm flex items-center gap-2 rounded-lg px-2 text-ink hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            :aria-label="t('session.account')"
          >
            <ElIcon :size="18"><User /></ElIcon>
            <span class="hidden max-w-48 truncate sm:inline">{{ session.displayName.value }}</span>
          </button>
          <template #dropdown>
            <ElDropdownMenu>
              <div class="px-4 py-2">
                <p class="body-sm font-medium text-ink">{{ session.displayName.value }}</p>
                <p class="mono-md text-ink-secondary">{{ session.email.value }}</p>
                <p class="body-xs mt-1 text-ink-secondary">
                  {{ t('session.role') }}: <span class="mono-md text-ink">{{ session.roleName.value }}</span>
                </p>
              </div>
              <ElDropdownItem divided :icon="SwitchButton" @click="signOut">
                {{ t('session.signOut') }}
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </header>

    <div class="flex min-h-0 flex-1">
      <!-- Permanent sidebar from lg upwards. -->
      <nav
        v-if="hasNav"
        class="hidden shrink-0 overflow-hidden border-r border-border bg-surface-raised transition-[width,opacity] duration-200 ease-out motion-reduce:transition-none lg:block"
        :class="collapsed ? 'w-0 border-r-0 opacity-0' : 'w-64 opacity-100'"
        :aria-label="t('nav.sections.viewer')"
        :aria-hidden="collapsed"
        :inert="collapsed"
        :data-collapsed="collapsed"
        data-testid="sidebar"
      >
       <div class="flex h-full w-64 flex-col overflow-y-auto px-4 pt-6 pb-4">
        <AppNavSection v-if="viewerItems.length" :title="t('nav.sections.viewer')" :items="viewerItems" />
        <hr v-if="viewerItems.length && adminItems.length" class="mx-3 mt-6 border-border" />
        <AppNavSection
          v-if="adminItems.length"
          class="mt-6"
          :title="t('nav.sections.administration')"
          :items="adminItems"
        />

        <!-- Footer: the plazas in the user's scope, pinned to the bottom. -->
        <div class="mt-auto px-3 pt-8" data-testid="plazas-footer">
          <p class="label-caps pb-1">{{ t('nav.plazas') }}</p>
          <span
            class="body-xs inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-ink-secondary"
          >
            <span class="size-1.5 shrink-0 rounded-full bg-status-available" aria-hidden="true" />
            <span class="truncate">{{ plazasLabel }}</span>
          </span>
        </div>
       </div>
      </nav>

      <!-- The same navigation in a slide-over below lg. -->
      <AppMobileMenu v-if="hasNav" v-model:open="isMenuOpen">
        <AppNavSection v-if="viewerItems.length" :title="t('nav.sections.viewer')" :items="viewerItems" />
        <hr v-if="viewerItems.length && adminItems.length" class="mx-3 mt-6 border-border" />
        <AppNavSection
          v-if="adminItems.length"
          class="mt-6"
          :title="t('nav.sections.administration')"
          :items="adminItems"
        />
        <p class="body-xs mt-8 px-3 text-ink-secondary">{{ t('nav.plazas') }}: {{ plazasLabel }}</p>
        <ElButton v-if="!session.isDevMode.value" class="touch-target mt-4 ml-3" @click="signOut">
          {{ t('session.signOut') }}
        </ElButton>
      </AppMobileMenu>

      <!-- No padding here: the viewer fills the area edge to edge and the
           other screens add their own. -->
      <main class="min-w-0 flex-1 overflow-y-auto">
        <RouterView />
      </main>
    </div>
  </div>
</template>
